import { diffWords } from '@/lib/text/word-diff';
import { ITEM_META } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface TextDiffProps {
  before: string;
  after: string;
  className?: string;
  /**
   * `inline` (default): one paragraph, deletions and insertions interleaved in place. Right for a caption changed by
   * a word or two. `split`: the text before and the text after as two stacked blocks, the removed words marked in the
   * first and the added words marked in the second, so each block reads as a sentence. Right for a passage rewritten
   * in several places, where interleaved old and new words stop being readable.
   */
  mode?: 'inline' | 'split';
}

/**
 * Word-level diff for short, prose-length before/after pairs (a caption before and after a rewrite, a sentence an
 * edit replaced), not a code-scale differ.
 */
export function TextDiff({
  before,
  after,
  className,
  mode = 'inline',
}: TextDiffProps): JSX.Element {
  const segments = diffWords(before, after);
  if (mode === 'split') {
    const block = (side: 'before' | 'after') => (
      <p
        data-diff-side={side}
        className={cn('whitespace-pre-wrap text-sm leading-snug text-foreground', className)}
      >
        {segments.map((segment, index) => {
          if (segment.type === 'equal') return <span key={index}>{segment.value}</span>;
          if (side === 'before' && segment.type === 'del')
            return (
              <span key={index} className="bg-destructive/10 text-destructive">
                {segment.value}
              </span>
            );
          if (side === 'after' && segment.type === 'add')
            return (
              <span key={index} className="bg-success/10 text-success">
                {segment.value}
              </span>
            );
          return null;
        })}
      </p>
    );
    return (
      <div className="min-w-0 max-w-full space-y-2">
        <div className="space-y-0.5">
          <p className={ITEM_META}>Before</p>
          {block('before')}
        </div>
        <div className="space-y-0.5">
          <p className={ITEM_META}>After</p>
          {block('after')}
        </div>
      </div>
    );
  }
  return (
    <p className={cn('whitespace-pre-wrap text-sm leading-snug text-foreground', className)}>
      {segments.map((segment, index) => {
        if (segment.type === 'equal') {
          return <span key={index}>{segment.value}</span>;
        }
        if (segment.type === 'add') {
          return (
            <span key={index} className="bg-success/10 text-success">
              {segment.value}
            </span>
          );
        }
        return (
          <span key={index} className="bg-destructive/10 text-destructive line-through">
            {segment.value}
          </span>
        );
      })}
    </p>
  );
}
